import { resolveId } from '@medplum/core'
import { referenceDisplay } from '@seen/core/display'
import type { BasePlan } from '@seen/core/models/care-plans/BasePlan'
import type { GoalCarePlan } from '@seen/core/models/care-plans/GoalCarePlan'
import { Intervention } from '@seen/core/models/care-plans/intervention'
import type { ExtendedCondition } from '@seen/core/models/conditions/ExtendedCondition'
import { Badge, Button, Separator } from '@seen/ui'
import {
  IconArrowDown,
  IconArrowUp,
  IconDotsVertical,
} from '@tabler/icons-react'
import { sortBy } from 'lodash-es'
import type { JSX } from 'react'
import { AddConditionButton } from '#src/components/care-plan/care-plan-goal/AddConditionButton.tsx'
import { CarePlanCondition } from '#src/components/care-plan/care-plan-goal/CarePlanCondition.tsx'
import { CarePlanCard } from '#src/components/care-plan/CarePlanCard.tsx'
import { Disclosure } from '#src/components/core/disclosure/Disclosure.tsx'
import { DisclosureContent } from '#src/components/core/disclosure/DisclosureContent.tsx'
import { DisclosureTrigger } from '#src/components/core/disclosure/DisclosureTrigger.tsx'
import { DropdownMenu } from '#src/components/core/dropdown-menu/DropdownMenu.tsx'
import { DropdownMenuContent } from '#src/components/core/dropdown-menu/DropdownMenuContent.tsx'
import { DropdownMenuDeleteItem } from '#src/components/core/dropdown-menu/DropdownMenuDeleteItem.tsx'
import { DropdownMenuItem } from '#src/components/core/dropdown-menu/DropdownMenuItem.tsx'
import { DropdownMenuSeparator } from '#src/components/core/dropdown-menu/DropdownMenuSeparator.tsx'
import { DropdownMenuTrigger } from '#src/components/core/dropdown-menu/DropdownMenuTrigger.tsx'
import { ResourceFieldForm } from '#src/components/core/form/ResourceFieldForm.tsx'
import { InterventionList } from '#src/components/InterventionList.tsx'
import { DevMedplumAppLinkOverlay } from '#src/components/medplum/DevMedplumAppLinkOverlay.tsx'
import { RichTextEditor } from '#src/components/rich-text-editor/RichTextEditor.tsx'
import { useCreateMutation } from '#src/hooks/useCreateMutation.ts'
import { useIsReadOnly } from '#src/hooks/useIsReadOnly.ts'
import { usePatchMutation } from '#src/hooks/usePatchMutation.ts'
import { usePortalStore } from '#src/hooks/usePortalStore.ts'

export function CarePlanGoal({
  goalNumber,
  goalCarePlan,
  onMoveUp,
  onMoveDown,
  isFirst,
  isLast,
  onDeleteCondition,
  interventions = [],
  allGoalCarePlans,
}: {
  goalNumber: number
  goalCarePlan: GoalCarePlan
  onMoveUp?: () => void
  onMoveDown?: () => void
  isFirst: boolean
  isLast: boolean
  onDeleteCondition?: (carePlan: BasePlan, condition: ExtendedCondition) => void
  interventions?: Intervention[]
  allGoalCarePlans: GoalCarePlan[]
}): JSX.Element {
  const showAdminActions = usePortalStore((state) => state.showAdminActions)
  const isReadOnly = useIsReadOnly()
  const patchGoalMutation = usePatchMutation<GoalCarePlan>()

  const createIntervention = useCreateMutation<Intervention>()

  const deleteGoal = (): void =>
    patchGoalMutation.mutate({
      instance: goalCarePlan,
      mutateFn(goalCarePlan) {
        goalCarePlan.status = 'entered-in-error'
      },
    })

  return (
    <CarePlanCard
      className="flex flex-col gap-4"
      showActions={showAdminActions}
      name={goalCarePlan.title}
      type="goal"
    >
      <div className="flex flex-col gap-4">
        <div className="flex items-center justify-between gap-2">
          <Badge variant="secondary" className="justify-self-start">
            Goal {goalNumber}
          </Badge>
          <div className="flex items-center gap-2">
            {!isReadOnly && goalCarePlan.isEditable && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm" className="shrink-0">
                    <IconDotsVertical className="size-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  align="end"
                  onClick={(e) => e.stopPropagation()}
                >
                  <DropdownMenuItem
                    onClick={onMoveUp}
                    disabled={isFirst}
                    className="flex items-center"
                  >
                    <IconArrowUp className="mr-2 size-4" />
                    Move Up
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={onMoveDown}
                    disabled={isLast}
                    className="flex items-center"
                  >
                    <IconArrowDown className="mr-2 size-4" />
                    Move Down
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuDeleteItem
                    deleteConfirmationProps={{
                      title: `Delete ${goalCarePlan.title}?`,
                      description:
                        'Deleted goals can still be recovered by contacting our team.',
                      onConfirm: deleteGoal,
                    }}
                  />
                </DropdownMenuContent>
              </DropdownMenu>
            )}
            <DevMedplumAppLinkOverlay to={goalCarePlan} inline />
          </div>
        </div>
        <ResourceFieldForm
          instance={goalCarePlan}
          valueFn={(gcp) => gcp.title}
          patchFn={(gcp, title) => {
            gcp.title = title
          }}
          debounceTyping
          render={({ field: { value, ...rest } }) => (
            <RichTextEditor
              defaultValue={value}
              placeholder="Enter title"
              variant="h3"
              editable={goalCarePlan.isEditable}
              {...rest}
            />
          )}
        />
        {(goalCarePlan.isEditable || goalCarePlan.description) && (
          <ResourceFieldForm
            instance={goalCarePlan}
            valueFn={(gcp) => gcp.description}
            patchFn={(gcp, description) => {
              gcp.description = description
            }}
            debounceTyping
            render={({ field: { value, ...rest } }) => (
              <RichTextEditor
                placeholder="Enter description"
                variant="underline"
                className="text-foreground-secondary"
                defaultValue={value}
                editable={goalCarePlan.isEditable}
                {...rest}
              />
            )}
          />
        )}
      </div>
      <Disclosure defaultOpen={goalCarePlan.conditions.length > 0}>
        <div className="flex items-center justify-between gap-4">
          <DisclosureTrigger className="p-0">
            <div className="flex grow items-center gap-2">
              <h4>Related Problems</h4>
              <Badge variant="secondary">
                {goalCarePlan.conditions.length ?? 0}
              </Badge>
            </div>
          </DisclosureTrigger>
          {!isReadOnly && goalCarePlan.isEditable && (
            <AddConditionButton
              carePlan={goalCarePlan}
              allGoalCarePlans={allGoalCarePlans}
              variant="outline"
            />
          )}
        </div>
        <DisclosureContent className="p-0">
          <div className="flex flex-col">
            {sortBy(goalCarePlan.conditions, (c) => referenceDisplay(c)).map(
              (condition) => (
                <CarePlanCondition
                  key={resolveId(condition)}
                  condition={condition}
                  carePlan={goalCarePlan}
                  onDelete={onDeleteCondition}
                />
              )
            )}
          </div>
        </DisclosureContent>
      </Disclosure>
      <Separator />
      <div className="flex flex-col gap-2">
        <div className="flex items-center justify-between gap-6">
          <div className="flex items-center gap-2">
            <h4>Interventions</h4>
            <Badge variant="secondary">{interventions.length}</Badge>
          </div>
          {!isReadOnly && goalCarePlan.isEditable && (
            <Button
              variant="outline"
              onClick={() => {
                createIntervention.mutate(
                  new Intervention({
                    goalCarePlan: goalCarePlan.reference,
                    subject: goalCarePlan.patientSubject,
                    order: interventions.length + 1,
                  })
                )
              }}
            >
              Add Intervention
            </Button>
          )}
        </div>
        {!!interventions.length && (
          <div className="-mx-4">
            <InterventionList interventions={interventions} />
          </div>
        )}
      </div>
    </CarePlanCard>
  )
}
